// Hooks
import { useAppSelector } from '../../../app/hooks';

// Features
import { selectUser } from '../../../features/user/userSlice';

// Models
import { Pattern } from '../../../features/session/sessionModels';

// Styles
import styles from './Card.module.css';

/**
 * @function Card
 * Describes the number sequence allotted to a single card
 *
 * @example
 * return (
 *   <Card
 *    id={1}
 *    numbers={[]}
 *    validNumbers={[]}
 *    daubedNumbers={[]}
 *    remaining={0}
 *    manual={false}
 *    columns={5}
 *    patterns={[]}
 *    wildcards={[]}
 *    onClick={() => false}
 *   />
 * )
 */
function Card({
    id,
    numbers,
    validNumbers,
    daubedNumbers,
    remaining,
    manual,
    columns,
    patterns = [],
    wildcards = [],
    onClick = () => false,
}: CardProps): JSX.Element {
    const user = useAppSelector(selectUser);
    const title: string =
        manual === false && remaining > 0 && remaining <= 4
            ? `${remaining}TOGO`
            : 'BINGO';
    const titleArr: string[] = title.split('');
    const dauberColor =
        user.dauber_color !== '0' && user.dauber_color !== ''
            ? user.dauber_color
            : 'blue';
    const dauberIcon = user.dauber_icon !== '0' ? user.dauber_icon : '';

    return (
        <article
            className={`${styles.container} ${
                manual === false && remaining >= 0 && remaining <= 4
                    ? ` ${styles[`card-${remaining}-to-go`]}`
                    : ''
            }`}
        >
            <header className={styles.header}>
                {titleArr.map((item, index) => (
                    <div key={`${item}-${index}`} className={styles.number}>
                        <span>{item}</span>
                    </div>
                ))}
            </header>
            <div className={styles.body}>
                {numbers.map((item, index) => {
                    const inPattern =
                        patterns.length === 1
                            ? patterns[0].pattern.variants.length === 1
                                ? patterns[0].pattern.variants[0].sequence.pattern.includes(
                                      index + 1,
                                  )
                                    ? true
                                    : false
                                : false
                            : false;
                    const isWildcard = wildcards.includes(index);
                    const char = isWildcard ? '★' : item;
                    const daubed = isWildcard
                        ? true
                        : manual
                        ? daubedNumbers.includes(item)
                        : validNumbers.includes(item);
                    const className =
                        styles.number +
                        (inPattern ? ` ${styles.pattern}` : '') +
                        (daubed
                            ? ` ${styles.daubed} ${
                                  dauberIcon !== '' ? styles.icon : ''
                              }`
                            : '');
                    const width = 100 / columns;

                    return (
                        <div
                            key={item}
                            className={className}
                            style={{
                                flex: `0 0 ${width}%`,
                                width: `${width}%`,
                                paddingTop: `${width}%`,
                            }}
                            onClick={() => onClick(id, item)}
                        >
                            {dauberIcon !== '' ? (
                                <span
                                    style={
                                        daubed
                                            ? {
                                                  backgroundImage: `url(https://${process.env.REACT_APP_CDN}/play-bingo/images/daubers/${dauberIcon}.svg)`,
                                                  fontSize: '0',
                                              }
                                            : {}
                                    }
                                >
                                    {char}
                                </span>
                            ) : (
                                <span
                                    style={
                                        daubed
                                            ? {
                                                  backgroundColor: dauberColor
                                                      ? `var(--color-${dauberColor})`
                                                      : '',
                                              }
                                            : {}
                                    }
                                >
                                    {char}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </article>
    );
}

/**
 * @interface CardProps
 * Describes the properties required for the Card component
 *
 * @property {number} id Unique identifier for card
 * @property {array} numbers Number sequence of card
 * @property {array} wildcards List of wildcard numbers
 * @property {array} validNumbers List of valid numbers numbers in draw sequence
 * @property {array} daubedNumbers List of daubed numbers in sequence
 * @property {number} remaining Number of daubed numbers remaining to win pattern
 * @property {boolean} manual Indicated manual or automatic daubing on cards
 * @property {array} rows Number of rows in the pattern grid
 * @property {array} columns Number of columns the pattern grid
 * @property {array} patterns List of patterns and their variants
 * @property {function} onClick Return function to call when clicked
 */
export interface CardProps {
    id: number;
    numbers: number[];
    wildcards: number[];
    validNumbers: number[];
    daubedNumbers: number[];
    remaining: number;
    manual: boolean;
    rows: number;
    columns: number;
    patterns?: Pattern[];
    // onClick?: MouseEventHandler<HTMLButtonElement>;
    onClick?: any;
}

// Exports
export default Card;
